import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { citaService } from '../services/citaService'
import { mascotaService } from '../services/mascotaService'
import { clinicaService } from '../services/clinicaService'
import { veterinarioService } from '../services/veterinarioService'
import { suscripcionClinicaService } from '../services/suscripcionClinicaService'
import { fichaService } from '../services/fichaService'
import Table, { TableHeader, TableBody, TableRow, TableCell, TableHead } from '../components/ui/Table'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Badge from '../components/ui/Badge'
import ActionsMenu from '../components/ui/ActionsMenu'
import EmptyState from '../components/ui/EmptyState'
import LoadingState from '../components/ui/LoadingState'
import Input from '../components/ui/Input'
import Select from '../components/ui/Select'
import Listbox from '../components/ui/Listbox'
import Combobox from '../components/ui/Combobox'
import Disclosure from '../components/ui/Disclosure'
import { 
  Calendar, Plus, Eye, RefreshCw, AlertCircle, CheckCircle, XCircle, Clock, Heart, Building2, User, Filter, X, Info, Download, AlertTriangle
} from 'lucide-react'
import Avatar from '../components/ui/Avatar'
import {
  TIPO_CITA_OPTIONS,
  DEFAULT_TIPO_CITA,
  getTipoCitaLabel,
  TIPO_CITA_BADGE_VARIANT,
  isTipoCitaClinicaRequired
} from '../constants/tipoCita'

export default function Citas() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const {
    isDueno,
    isVeterinario,
    canAprobarCita,
    canAsignarFechaCita,
    canCompletarCita,
    canRechazarCita,
  } = usePermissions()

  const esSolicitudPetSitter = (cita) =>
    String(cita?.tipo_solicitante || cita?.tipoSolicitante || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_') === 'PET_SITTER'
  const [citas, setCitas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    estado: '',
    tipo_cita: '',
    id_clinica: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showAsignarFechaModal, setShowAsignarFechaModal] = useState(false)
  const [showCompletarModal, setShowCompletarModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedCitaId, setSelectedCitaId] = useState(null)
  const [selectedCita, setSelectedCita] = useState(null)
  const [selectedCitaForFecha, setSelectedCitaForFecha] = useState(null) // Cita seleccionada para asignar fecha
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [downloadingPDF, setDownloadingPDF] = useState(null) // ID de la ficha que se está descargando
  const [rejectMotivo, setRejectMotivo] = useState('')
  const [completarData, setCompletarData] = useState({
    observaciones: '',
    id_ficha: ''
  })
  const [fichasDisponibles, setFichasDisponibles] = useState([])
  const [fechaHoraData, setFechaHoraData] = useState({
    fecha_cita_propuesta: '',
    hora_cita_propuesta: ''
  })
  const [mascotas, setMascotas] = useState([])
  const [clinicas, setClinicas] = useState([])
  const [clinicasSuscritas, setClinicasSuscritas] = useState([])
  const [veterinarios, setVeterinarios] = useState([])
  const [formData, setFormData] = useState({
    id_mascota: '',
    id_clinica: '',
    id_veterinario: '',
    tipo_cita: DEFAULT_TIPO_CITA,
    descripcion_cita: '',
    fecha_cita_propuesta: '',
    hora_cita_propuesta: ''
  })
  const [isEmergencia, setIsEmergencia] = useState(false)
  const [motivoEmergencia, setMotivoEmergencia] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const clinicaRequerida = isEmergencia || isTipoCitaClinicaRequired(formData.tipo_cita)

  useEffect(() => {
    fetchCitas()
    fetchMascotas()
    fetchClinicas()
  }, [pagination.current_page, filtros, user])

  useEffect(() => {
    if (formData.id_clinica) {
      fetchVeterinarios(formData.id_clinica)
    } else {
      setVeterinarios([])
    }
  }, [formData.id_clinica])

  const fetchCitas = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.estado && { estado: filtros.estado }),
        ...(filtros.tipo_cita && { tipo_cita: filtros.tipo_cita }),
        ...(filtros.id_clinica && { id_clinica: filtros.id_clinica })
      }

      // Si es veterinario, el backend filtra por clínica automáticamente
      // No necesitamos enviar id_veterinario como parámetro

      const response = await citaService.getAll(params)
      
      if (response.success) {
        if (response.data && response.data.data) {
          // Respuesta paginada
          setCitas(response.data.data)
          setPagination({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else if (Array.isArray(response.data)) {
          // Respuesta como array directo
          setCitas(response.data)
          setPagination({
            current_page: 1,
            per_page: response.data.length,
            total: response.data.length,
            last_page: 1
          })
        } else {
          // No hay datos
          setCitas([])
          setPagination({
            current_page: 1,
            per_page: 15,
            total: 0,
            last_page: 1
          })
        }
      } else {
        setError(response.error || 'Error al cargar citas')
        setCitas([])
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching citas:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMascotas = async () => {
    try {
      // Si es DUEÑO, solo obtener sus mascotas
      const params = { per_page: 100 }
      if (isDueno) {
        params.id_dueño = user?.idUsuario || user?.id_usuario
      }
      const response = await mascotaService.getAll(params)
      if (response.success) {
        setMascotas(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching mascotas:', err)
    }
  }

  const fetchClinicas = async () => {
    try {
      // Si es veterinario, solo obtener su clínica
      const params = { per_page: 100 }
      if (isVeterinario && user?.veterinarios?.[0]?.id_clinica) {
        params.id_clinica = user.veterinarios[0].id_clinica
      }
      const response = await clinicaService.getAll(params)
      if (response.success) {
        const clinicasData = response.data.data || response.data || []
        setClinicas(clinicasData)
        // Si es veterinario y solo hay una clínica, establecerla automáticamente en filtros
        if (isVeterinario && clinicasData.length === 1 && !filtros.id_clinica) {
          setFiltros(prev => ({ ...prev, id_clinica: clinicasData[0].id_clinica }))
        }
      }
    } catch (err) {
      console.error('Error fetching clínicas:', err)
    }
  }

  const fetchVeterinarios = async (idClinica) => {
    try {
      const response = await veterinarioService.getAll({ id_clinica: idClinica, per_page: 100 })
      if (response.success) {
        setVeterinarios(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching veterinarios:', err)
    }
  }

  const handleCreate = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      if (!formData.id_mascota) {
        setError('Debes seleccionar una mascota para crear la cita')
        setSubmitting(false)
        return
      }

      let response
      
      if (isEmergencia) {
        // Validar motivo de emergencia
        if (!motivoEmergencia.trim()) {
          setError('El motivo de emergencia es requerido')
          setSubmitting(false)
          return
        }
        
        // Crear cita de emergencia
        response = await citaService.createEmergencia({
          ...formData,
          motivo_emergencia: motivoEmergencia
        })
      } else {
        if (clinicaRequerida && !formData.id_clinica) {
          setError('Debes seleccionar una clínica para este tipo de cita')
          setSubmitting(false)
          return
        }

        // Crear cita normal
        response = await citaService.create(formData)
      }
      
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        setIsEmergencia(false)
        setMotivoEmergencia('')
        // Forzar refresh de citas y resetear a la primera página
        setPagination(prev => ({ ...prev, current_page: 1 }))
        // Pequeño delay para asegurar que el backend haya procesado la cita
        setTimeout(() => {
          fetchCitas()
        }, 500)
        
        // Mostrar mensaje especial si se creó suscripción temporal
        if (isEmergencia && response.data?.suscripcion_temporal) {
          const suscripcion = response.data.suscripcion_temporal
          const mensaje = suscripcion.estado === 'PENDIENTE_APROBACION'
            ? 'Cita de emergencia creada. La suscripción temporal está pendiente de aprobación por la clínica.'
            : `Cita de emergencia creada. Se ha creado una suscripción temporal que expira el ${new Date(suscripcion.fecha_expiracion).toLocaleDateString()}.`
          
          // Podríamos mostrar un toast aquí, por ahora solo en consola
          console.log(mensaje)
        }
      } else {
        setError(response.error || 'Error al crear cita')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al crear cita')
      console.error('Error al crear cita:', err)
    } finally {
      setSubmitting(false)
    }
  }

  const handleAprobar = async (id) => {
    try {
      console.log('🔍 Intentando aprobar cita con ID:', id)
      setError(null) // Limpiar errores previos
      const response = await citaService.aprobar(id)
      console.log('📥 Respuesta del servidor:', response)
      if (response.success) {
        console.log('✅ Cita aprobada exitosamente')
        // Abrir modal para asignar fecha y hora
        setSelectedCitaId(id)
        // Cargar los detalles de la cita para mostrar la fecha y hora propuesta original
        try {
          const detailResponse = await citaService.getById(id)
          if (detailResponse.success && detailResponse.data) {
            const citaDetalle = detailResponse.data
            setSelectedCitaForFecha(citaDetalle) // Guardar la cita completa
            
            // Pre-llenar con la fecha y hora propuesta original del cliente
            const fechaOriginal = citaDetalle.fecha_cita_propuesta || citaDetalle.fechaCitaPropuesta
            const horaOriginal = citaDetalle.hora_cita_propuesta || citaDetalle.horaCitaPropuesta
            
            // Convertir fecha a formato YYYY-MM-DD si existe
            let fechaFormato = ''
            if (fechaOriginal) {
              const fecha = new Date(fechaOriginal)
              fechaFormato = fecha.toISOString().split('T')[0]
            }
            
            setFechaHoraData({ 
              fecha_cita_propuesta: fechaFormato, 
              hora_cita_propuesta: horaOriginal || '' 
            })
          } else {
            setSelectedCitaForFecha(null)
            setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
          }
        } catch (err) {
          console.error('Error al cargar detalles de la cita:', err)
          setSelectedCitaForFecha(null)
          setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
        }
        setShowAsignarFechaModal(true)
        fetchCitas()
      } else {
        const errorMsg = response.error || 'Error al aprobar cita'
        console.error('❌ Error en respuesta:', errorMsg)
        setError(errorMsg)
      }
    } catch (err) {
      console.error('❌ Error al aprobar cita:', err)
      const errorMsg = err.response?.data?.error || err.message || 'Error al aprobar cita'
      setError(errorMsg)
    }
  }

  const handleAsignarFechaHora = async () => {
    if (!fechaHoraData.fecha_cita_propuesta || !fechaHoraData.hora_cita_propuesta) {
      setError('Debe ingresar fecha y hora')
      return
    }

    try {
      setError(null)
      const response = await citaService.asignarFechaHora(selectedCitaId, fechaHoraData)
      if (response.success) {
        setShowAsignarFechaModal(false)
        setSelectedCitaId(null)
        setSelectedCitaForFecha(null)
        setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
        fetchCitas()
      } else {
        setError(response.error || 'Error al asignar fecha y hora')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al asignar fecha y hora')
    }
  }

  const [downloadingCalendar, setDownloadingCalendar] = useState(null) // ID de la cita que se está descargando

  const handleDescargarCalendario = async (id) => {
    try {
      setDownloadingCalendar(id)
      setError(null)
      await citaService.descargarCalendario(id)
      // No necesitamos mostrar mensaje de éxito ya que el navegador maneja la descarga
      // En algunos dispositivos móviles, el archivo se abrirá directamente en la app de calendario
    } catch (err) {
      const errorMsg = err.message || 'Error al descargar archivo de calendario'
      setError(errorMsg)
      console.error('Error al descargar calendario:', err)
    } finally {
      // Resetear después de un pequeño delay para que el usuario vea el feedback
      setTimeout(() => {
        setDownloadingCalendar(null)
      }, 500)
    }
  }

  const handleCompletar = async () => {
    if (!selectedCitaId) return

    try {
      setError(null)
      const response = await citaService.completar(selectedCitaId, completarData)
      if (response.success) {
        setShowCompletarModal(false)
        setSelectedCitaId(null)
        setCompletarData({ observaciones: '', id_ficha: '' })
        fetchCitas()
      } else {
        setError(response.error || 'Error al completar cita')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al completar cita')
    }
  }

  const handleOpenCompletarModal = async (id) => {
    setSelectedCitaId(id)
    setCompletarData({ observaciones: '', id_ficha: '' })
    // Aquí podrías cargar las fichas clínicas disponibles si es necesario
    setShowCompletarModal(true)
  }

  const handleVerDetalles = async (id) => {
    try {
      setLoadingDetail(true)
      setError(null)
      const response = await citaService.getById(id)
      if (response.success) {
        setSelectedCita(response.data)
        setShowDetailModal(true)
      } else {
        setError(response.error || 'Error al cargar detalles de la cita')
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al cargar detalles de la cita')
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleRechazar = async () => {
    if (!rejectMotivo.trim()) {
      setError('Debe ingresar un motivo para rechazar la cita')
      return
    }

    // Validar que el motivo tenga al menos 10 caracteres (según validación del backend)
    if (rejectMotivo.trim().length < 10) {
      setError('El motivo de rechazo debe tener al menos 10 caracteres')
      return
    }

    try {
      setError(null)
      const response = await citaService.rechazar(selectedCitaId, rejectMotivo.trim())
      if (response.success) {
        setShowRejectModal(false)
        setSelectedCitaId(null)
        setRejectMotivo('')
        fetchCitas()
      } else {
        const errorMsg = response.error || response.message || 'Error al rechazar cita'
        setError(errorMsg)
      }
    } catch (err) {
      // Manejar errores del backend con más detalle
      const errorMsg = err.response?.data?.error || 
                      err.response?.data?.message || 
                      err.response?.data?.details?.motivo_rechazo?.[0] ||
                      err.message || 
                      'Error al rechazar cita'
      setError(errorMsg)
      console.error('Error al rechazar cita:', err.response?.data || err)
    }
  }

  const resetForm = () => {
    setFormData({
      id_mascota: '',
      id_clinica: '',
      id_veterinario: '',
      tipo_cita: DEFAULT_TIPO_CITA,
      descripcion_cita: '',
      fecha_cita_propuesta: '',
      hora_cita_propuesta: ''
    })
    setIsEmergencia(false)
    setMotivoEmergencia('')
    setVeterinarios([])
  }

  const handleOpenCreateModal = async () => {
    resetForm()
    
    // Si es DUEÑO, buscar su suscripción activa y pre-seleccionar la clínica
    if (isDueno) {
      try {
        const response = await suscripcionClinicaService.getAll()
        if (response.success) {
          const suscripciones = response.data.data || response.data || []
          const clinicasDeSuscripciones = suscripciones
            .map((sub) => sub?.clinica)
            .filter(Boolean)
            .filter((clinica, index, arr) => {
              const clinicaId = clinica.id_clinica || clinica.idClinica || clinica.id
              return arr.findIndex((item) => (item.id_clinica || item.idClinica || item.id) === clinicaId) === index
            })
          setClinicasSuscritas(clinicasDeSuscripciones)

          // Buscar la suscripción activa
          const suscripcionActiva = suscripciones.find(
            (sub) => sub.estado === 'ACTIVA'
          )
          
          if (suscripcionActiva && suscripcionActiva.id_clinica) {
            // Pre-seleccionar la clínica de la suscripción activa
            setFormData((prev) => ({
              ...prev,
              id_clinica: suscripcionActiva.id_clinica
            }))
            // Cargar los veterinarios de esa clínica
            fetchVeterinarios(suscripcionActiva.id_clinica)
          }
        }
      } catch (err) {
        console.error('Error al obtener suscripción activa:', err)
        // Si hay error, simplemente abrir el modal sin pre-seleccionar
        setClinicasSuscritas([])
      }
    }
    
    setShowCreateModal(true)
  }

  const clearFilters = () => {
    setFiltros({ estado: '', tipo_cita: '', id_clinica: '' })
  }

  const hasActiveFilters = filtros.estado || filtros.tipo_cita || filtros.id_clinica
  const clinicasParaSeleccion = useMemo(() => {
    if (!isDueno) return clinicas
    return clinicasSuscritas.length > 0 ? clinicasSuscritas : clinicas
  }, [isDueno, clinicasSuscritas, clinicas])

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 'PENDIENTE_APROBACION':
        return <Badge variant="warning">Pendiente Aprobación</Badge>
      case 'APROBADA':
        return <Badge variant="info">Aprobada</Badge>
      case 'CONFIRMADA':
        return <Badge variant="success">Confirmada</Badge>
      case 'COMPLETADA':
        return <Badge variant="success">Completada</Badge>
      case 'RECHAZADA':
        return <Badge variant="danger">Rechazada</Badge>
      case 'CANCELADA':
        return <Badge variant="default">Cancelada</Badge>
      default:
        return <Badge variant="default">{estado?.replace('_', ' ')}</Badge>
    }
  }

  const getTipoCitaBadge = (tipo) => {
    const label = getTipoCitaLabel(tipo)
    const variant = TIPO_CITA_BADGE_VARIANT[tipo] || 'default'
    return <Badge variant={variant}>{label}</Badge>
  }

  const filtroTipoCitaOptions = [{ value: '', label: 'Todos' }, ...TIPO_CITA_OPTIONS]

  if (loading && citas.length === 0) {
    return <LoadingState message="Cargando citas..." size="lg" />
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Appointments</p>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink bg-clip-text text-transparent">
            {isDueno ? 'Mis Citas' : 'Citas Veterinarias'}
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-pastel-ink/80 dark:text-gray-400">
            {isDueno 
              ? 'Gestiona las citas de tus mascotas'
              : 'Gestión completa de citas del sistema'}
          </p>
        </div>
        <Button onClick={handleOpenCreateModal} className="inline-flex items-center w-full sm:w-auto justify-center">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Cita
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            {error}
          </div>
          <button onClick={() => setError(null)} className="ml-4">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Filtros */}
      <Disclosure 
        title="Filtros de Búsqueda"
        defaultOpen={hasActiveFilters}
        className="bg-white/95 dark:bg-gray-800 rounded-3xl border border-pastel-pink dark:border-gray-700 shadow-soft"
      >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Listbox
              label="Estado"
              options={[
                { value: '', label: 'Todos' },
                { value: 'PENDIENTE_APROBACION', label: 'Pendiente Aprobación' },
                { value: 'APROBADA', label: 'Aprobada' },
                { value: 'CONFIRMADA', label: 'Confirmada' },
                { value: 'COMPLETADA', label: 'Completada' },
                { value: 'RECHAZADA', label: 'Rechazada' },
                { value: 'CANCELADA', label: 'Cancelada' }
              ]}
              value={filtros.estado}
              onChange={(value) => setFiltros({ ...filtros, estado: value })}
            />
            <Listbox
              label="Tipo de Cita"
              options={filtroTipoCitaOptions}
              value={filtros.tipo_cita}
              onChange={(value) => setFiltros({ ...filtros, tipo_cita: value })}
            />
            <Listbox
              label="Clínica"
              options={[
                { value: '', label: 'Todas' },
                ...clinicas.map((clinica) => ({
                  value: clinica.id_clinica || clinica.idClinica,
                  label: clinica.nombre
                }))
              ]}
              value={filtros.id_clinica}
              onChange={(value) => setFiltros({ ...filtros, id_clinica: value })}
            />
            <div className="flex items-end">
              <Button onClick={fetchCitas} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="flex justify-end">
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpiar Filtros
              </Button>
            </div>
          )}
        </div>
      </Disclosure>

      {/* Tabla */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto overflow-y-visible">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead className="hidden sm:table-cell">Hora</TableHead>
                <TableHead>Mascota</TableHead>
                <TableHead className="hidden md:table-cell">Clínica</TableHead>
                <TableHead className="hidden lg:table-cell">Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {citas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan="7">
                    <EmptyState
                      icon={Calendar}
                      title="No hay citas registradas"
                      description="Comienza creando una nueva cita para una mascota"
                      action={
                        <Button onClick={handleOpenCreateModal}>
                          <Plus className="w-4 h-4 mr-2" />
                          Crear Cita
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ) : (
                citas.map((cita) => (
                  <TableRow key={cita.id_cita || cita.idCita}>
                    <TableCell>
                      {cita.fecha_cita_propuesta || cita.fechaCitaPropuesta ? (
                        <div className="flex items-center space-x-2">
                          <Calendar className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                          <span className="text-pastel-ink dark:text-white">
                            {new Date(cita.fecha_cita_propuesta || cita.fechaCitaPropuesta).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      ) : (
                        <span className="text-pastel-ink/60 dark:text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {cita.hora_cita_propuesta || cita.horaCitaPropuesta ? (
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                          <span className="text-pastel-ink dark:text-white">
                            {cita.hora_cita_propuesta || cita.horaCitaPropuesta}
                          </span>
                        </div>
                      ) : (
                        <span className="text-pastel-ink/60 dark:text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {cita.mascota ? (
                        <div className="flex items-center space-x-2">
                          <Heart className="w-4 h-4 text-pastel-teal dark:text-primary-400" />
                          <div>
                            <div className="font-medium text-pastel-ink dark:text-white">
                              {cita.mascota.nombre}
                            </div>
                            <div className="text-xs text-pastel-ink/70 dark:text-gray-400 sm:hidden">
                              {cita.mascota.especie}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-pastel-ink/60 dark:text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {cita.clinica ? (
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-pastel-ink/60 dark:text-gray-500" />
                          <span className="text-pastel-ink dark:text-white">
                            {cita.clinica.nombre || cita.clinica.name}
                          </span>
                        </div>
                      ) : (
                        <span className="text-pastel-ink/60 dark:text-gray-500">-</span>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {getTipoCitaBadge(cita.tipo_cita || cita.tipoCita)}
                    </TableCell>
                    <TableCell>
                      {getEstadoBadge(cita.estado)}
                    </TableCell>
                    <TableCell align="right">
                      {(() => {
                        const citaId = cita.id_cita || cita.idCita
                        const estadoCita = cita.estado
                        // Pet sitter: solo el dueño aprueba/rechaza. Clínica/vet: vet/admin aprueban el resto.
                        const petSitterFlow = esSolicitudPetSitter(cita)
                        const puedeAprobar =
                          estadoCita === 'PENDIENTE_APROBACION' &&
                          ((canAprobarCita && !petSitterFlow) || (isDueno && petSitterFlow))
                        
                        const menuItems = [
                          {
                            label: 'Ver detalles',
                            icon: 'view',
                            onClick: () => handleVerDetalles(citaId)
                          }
                        ]
                        
                        // Descargar calendario:
                        // - Citas clínicas: CONFIRMADA + fecha/hora
                        // - Citas internas (sin clínica): APROBADA + fecha/hora
                        const estaConfirmada = cita.estado === 'CONFIRMADA' || cita.estado === 'Confirmada'
                        const estaAprobada = estadoCita === 'APROBADA' || estadoCita === 'Aprobada'
                        const esCitaInterna = !(cita.id_clinica || cita.idClinica)
                        const puedeDescargarCalendario = estaConfirmada || (esCitaInterna && estaAprobada)
                        const tieneFechaHora = cita.fecha_cita_propuesta || cita.fechaCitaPropuesta
                        const tieneHora = cita.hora_cita_propuesta || cita.horaCitaPropuesta
                        
                        if (puedeDescargarCalendario && tieneFechaHora && tieneHora) {
                          const estaDescargando = downloadingCalendar === citaId
                          menuItems.push({
                            label: estaDescargando ? 'Descargando...' : 'Descargar calendario',
                            icon: estaDescargando ? 'loading' : 'download',
                            onClick: () => handleDescargarCalendario(citaId),
                            disabled: downloadingCalendar !== null
                          })
                        }
                        
                        // Asignar fecha y hora: solo VET/ADMIN/SUPERADMIN (usePermissions)
                        const puedeAsignarFecha = canAsignarFechaCita && estaAprobada && !tieneFechaHora
                        
                        if (puedeAsignarFecha) {
                          menuItems.push({
                            label: 'Asignar fecha y hora',
                            icon: 'edit',
                            onClick: async () => {
                              setSelectedCitaId(citaId)
                              // Cargar los detalles de la cita para mostrar la fecha y hora propuesta original
                              try {
                                const detailResponse = await citaService.getById(citaId)
                                if (detailResponse.success && detailResponse.data) {
                                  const citaDetalle = detailResponse.data
                                  setSelectedCitaForFecha(citaDetalle) // Guardar la cita completa
                                  
                                  // Pre-llenar con la fecha y hora propuesta original del cliente
                                  const fechaOriginal = citaDetalle.fecha_cita_propuesta || citaDetalle.fechaCitaPropuesta
                                  const horaOriginal = citaDetalle.hora_cita_propuesta || citaDetalle.horaCitaPropuesta
                                  
                                  // Convertir fecha a formato YYYY-MM-DD si existe
                                  let fechaFormato = ''
                                  if (fechaOriginal) {
                                    const fecha = new Date(fechaOriginal)
                                    fechaFormato = fecha.toISOString().split('T')[0]
                                  }
                                  
                                  setFechaHoraData({ 
                                    fecha_cita_propuesta: fechaFormato, 
                                    hora_cita_propuesta: horaOriginal || '' 
                                  })
                                } else {
                                  setSelectedCitaForFecha(null)
                                  setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
                                }
                              } catch (err) {
                                console.error('Error al cargar detalles de la cita:', err)
                                setSelectedCitaForFecha(null)
                                setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' })
                              }
                              setShowAsignarFechaModal(true)
                            }
                          })
                        }
                        
                        // Completar cita: solo VET/ADMIN/SUPERADMIN (usePermissions)
                        const puedeCompletar = canCompletarCita && estaConfirmada
                        
                        if (puedeCompletar) {
                          menuItems.push({
                            label: 'Completar',
                            icon: 'check',
                            onClick: () => handleOpenCompletarModal(citaId)
                          })
                        }
                        
                        // Agregar opciones de aprobar/rechazar si aplica
                        if (puedeAprobar) {
                          menuItems.push(
                            {
                              label: 'Aprobar',
                              icon: 'edit',
                              onClick: () => {
                                if (handleAprobar && citaId) {
                                  handleAprobar(citaId)
                                } else {
                                  console.error('❌ handleAprobar o citaId no está disponible', { handleAprobar, citaId })
                                }
                              }
                            },
                            {
                              label: 'Rechazar',
                              icon: 'delete',
                              danger: true,
                              onClick: () => {
                                setSelectedCitaId(citaId)
                                setShowRejectModal(true)
                              }
                            }
                          )
                        }
                        
                        // Rechazar: mismo criterio que aprobar (dueño en flujo pet sitter; vet en el resto)
                        const puedeRechazar =
                          estadoCita === 'PENDIENTE_APROBACION' &&
                          ((canRechazarCita && !petSitterFlow) || (isDueno && petSitterFlow))

                        if (puedeRechazar && !puedeAprobar) {
                          menuItems.push({
                            label: 'Rechazar',
                            icon: 'delete',
                            danger: true,
                            onClick: () => {
                              setSelectedCitaId(citaId)
                              setShowRejectModal(true)
                            }
                          })
                        }
                        
                        return (
                          <ActionsMenu items={menuItems} />
                        )
                      })()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Paginación */}
      {pagination.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white dark:bg-gray-800 rounded-2xl border border-pastel-pink dark:border-gray-700 p-4 shadow-soft">
          <div className="text-sm text-pastel-ink dark:text-gray-300">
            Mostrando <span className="font-medium">{((pagination.current_page - 1) * pagination.per_page) + 1}</span> a{' '}
            <span className="font-medium">{Math.min(pagination.current_page * pagination.per_page, pagination.total)}</span> de{' '}
            <span className="font-medium">{pagination.total}</span> resultados
          </div>
          <div className="flex space-x-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === 1}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
            >
              Anterior
            </Button>
            <div className="px-3 py-1.5 text-sm font-medium text-pastel-ink dark:text-gray-300 flex items-center">
              Página {pagination.current_page} de {pagination.last_page}
            </div>
            <Button
              variant="secondary"
              size="sm"
              disabled={pagination.current_page === pagination.last_page}
              onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}

      {/* Modal Crear Cita */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="lg">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          Nueva Cita
        </ModalHeader>
        <form onSubmit={handleCreate}>
          <ModalContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Listbox
                  label="Mascota"
                  options={[
                    { value: '', label: 'Seleccione una mascota...' },
                    ...mascotas.map((mascota) => ({
                      value: mascota.id_mascota || mascota.idMascota,
                      label: `${mascota.nombre} (${mascota.especie})`
                    }))
                  ]}
                  value={formData.id_mascota}
                  onChange={(value) => setFormData({ ...formData, id_mascota: value })}
                  placeholder="Seleccione mascota..."
                />
              </div>
              <Listbox
                label={`Clínica${clinicaRequerida ? ' *' : ' (opcional)'}`}
                options={[
                  { value: '', label: 'Seleccione una clínica...' },
                  ...clinicasParaSeleccion.map((clinica) => ({
                    value: clinica.id_clinica || clinica.idClinica,
                    label: clinica.nombre
                  }))
                ]}
                value={formData.id_clinica}
                onChange={(value) => setFormData({ ...formData, id_clinica: value, id_veterinario: '' })}
                placeholder="Seleccione clínica..."
              />
              {isDueno && clinicasParaSeleccion.length === 0 && (
                <div className="md:col-span-2 -mt-2">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    No tienes clínicas disponibles para seleccionar. Suscríbete a una clínica antes de crear la cita.
                  </p>
                </div>
              )}
              <Listbox
                label="Veterinario"
                options={[
                  { value: '', label: 'Seleccione un veterinario...' },
                  ...veterinarios.map((vet) => ({
                    value: vet.id_veterinario || vet.idVeterinario,
                    label: vet.usuario ? `${vet.usuario.nombre} ${vet.usuario.apellido || ''}`.trim() : 'Veterinario'
                  }))
                ]}
                value={formData.id_veterinario}
                onChange={(value) => setFormData({ ...formData, id_veterinario: value })}
                disabled={!formData.id_clinica}
                placeholder="Seleccione veterinario..."
              />
              <Listbox
                label="Tipo de Cita"
                options={TIPO_CITA_OPTIONS}
                value={formData.tipo_cita}
                onChange={(value) => setFormData({ ...formData, tipo_cita: value })}
              />
              <Input
                label="Fecha Propuesta"
                type="date"
                required
                min={new Date().toISOString().split('T')[0]}
                value={formData.fecha_cita_propuesta}
                onChange={(e) => setFormData({ ...formData, fecha_cita_propuesta: e.target.value })}
              />
              <Input
                label="Hora Propuesta"
                type="time"
                required
                value={formData.hora_cita_propuesta}
                onChange={(e) => setFormData({ ...formData, hora_cita_propuesta: e.target.value })}
              />
              {/* Opción de Emergencia */}
              <div className="md:col-span-2">
                <div className="flex items-center space-x-3 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-pastel-peach/30 dark:bg-gray-800/50">
                  <input
                    type="checkbox"
                    id="is_emergencia"
                    checked={isEmergencia}
                    onChange={(e) => {
                      setIsEmergencia(e.target.checked)
                      if (!e.target.checked) {
                        setMotivoEmergencia('')
                      }
                    }}
                    className="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 dark:focus:ring-red-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                  />
                  <label htmlFor="is_emergencia" className="flex items-center space-x-2 cursor-pointer">
                    <AlertTriangle className="w-5 h-5 text-red-700 dark:text-red-400" />
                    <span className="text-sm font-medium text-pastel-ink dark:text-white">
                      Esta es una cita de emergencia
                    </span>
                  </label>
                </div>
                {isEmergencia && (
                  <div className="mt-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-700 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-red-900 dark:text-red-200 mb-2">
                          Cita de Emergencia
                        </p>
                        <p className="text-sm text-red-800 dark:text-red-300 mb-3">
                          Si la mascota no está suscrita a esta clínica, se creará automáticamente una suscripción temporal para permitir la atención de emergencia.
                        </p>
                        <div>
                          <label className="block text-sm font-medium text-red-900 dark:text-red-200 mb-1.5">
                            Motivo de la emergencia <span className="text-red-600">*</span>
                          </label>
                          <textarea
                            required={isEmergencia}
                            value={motivoEmergencia}
                            onChange={(e) => setMotivoEmergencia(e.target.value)}
                            rows={3}
                            className="input resize-none border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500"
                            placeholder="Describa brevemente el motivo de la emergencia..."
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1">
                      Fecha y hora de referencia
                    </p>
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      La fecha y hora seleccionadas son de referencia para la comodidad del dueño/pet sitter. 
                      La fecha y hora final de la cita pueden variar hasta que la clínica confirme la disponibilidad.
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-pastel-ink dark:text-gray-300 mb-1.5">
                  Descripción <span className="text-red-600">*</span>
                </label>
                <textarea
                  required
                  value={formData.descripcion_cita}
                  onChange={(e) => setFormData({ ...formData, descripcion_cita: e.target.value })}
                  rows={3}
                  className="input resize-none"
                  placeholder="Describa el motivo de la cita..."
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <Button type="button" variant="secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button type="submit" loading={submitting}>
              Crear Cita
            </Button>
          </ModalFooter>
        </form>
      </Modal>

      {/* Modal Asignar Fecha y Hora */}
      <Modal isOpen={showAsignarFechaModal} onClose={() => { setShowAsignarFechaModal(false); setSelectedCitaId(null); setSelectedCitaForFecha(null); setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' }); }} size="md">
        <ModalHeader onClose={() => { setShowAsignarFechaModal(false); setSelectedCitaId(null); setSelectedCitaForFecha(null); setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' }); }}>
          Asignar Fecha y Hora
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            {/* Mostrar fecha y hora propuesta original del cliente */}
            {selectedCitaForFecha && (selectedCitaForFecha.fecha_cita_propuesta || selectedCitaForFecha.fechaCitaPropuesta) && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-2">
                      Fecha y hora propuesta por el cliente:
                    </p>
                    <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>
                          {new Date(selectedCitaForFecha.fecha_cita_propuesta || selectedCitaForFecha.fechaCitaPropuesta).toLocaleDateString('es-ES', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      {(selectedCitaForFecha.hora_cita_propuesta || selectedCitaForFecha.horaCitaPropuesta) && (
                        <div className="flex items-center space-x-2">
                          <Clock className="w-4 h-4" />
                          <span>{selectedCitaForFecha.hora_cita_propuesta || selectedCitaForFecha.horaCitaPropuesta}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                      Puedes usar esta fecha y hora como referencia o seleccionar una diferente.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <Input
              label="Fecha de la Cita"
              type="date"
              required
              min={new Date().toISOString().split('T')[0]}
              value={fechaHoraData.fecha_cita_propuesta}
              onChange={(e) => setFechaHoraData({ ...fechaHoraData, fecha_cita_propuesta: e.target.value })}
            />
            <Input
              label="Hora de la Cita"
              type="time"
              required
              value={fechaHoraData.hora_cita_propuesta}
              onChange={(e) => setFechaHoraData({ ...fechaHoraData, hora_cita_propuesta: e.target.value })}
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={() => { setShowAsignarFechaModal(false); setSelectedCitaId(null); setSelectedCitaForFecha(null); setFechaHoraData({ fecha_cita_propuesta: '', hora_cita_propuesta: '' }); }}>
            Cancelar
          </Button>
          <Button onClick={handleAsignarFechaHora}>
            Asignar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Completar Cita */}
      <Modal isOpen={showCompletarModal} onClose={() => { setShowCompletarModal(false); setSelectedCitaId(null); setCompletarData({ observaciones: '', id_ficha: '' }); }} size="md">
        <ModalHeader onClose={() => { setShowCompletarModal(false); setSelectedCitaId(null); setCompletarData({ observaciones: '', id_ficha: '' }); }}>
          Completar Cita
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>Nota:</strong> Al completar la cita, se marcará como finalizada. Puedes agregar observaciones si el encuentro no ocurrió o si hay información adicional relevante.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-ink dark:text-gray-300 mb-2">
                Observaciones (opcional)
              </label>
              <textarea
                value={completarData.observaciones}
                onChange={(e) => setCompletarData({ ...completarData, observaciones: e.target.value })}
                rows={4}
                className="input resize-none"
                placeholder="Ej: El encuentro no ocurrió porque el dueño no asistió. O cualquier otra observación relevante..."
                maxLength={1000}
              />
              <p className="mt-1 text-xs text-pastel-ink/70 dark:text-gray-400">
                {completarData.observaciones.length}/1000 caracteres
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-ink dark:text-gray-300 mb-2">
                Ficha Clínica (opcional)
              </label>
              <Input
                type="number"
                value={completarData.id_ficha}
                onChange={(e) => setCompletarData({ ...completarData, id_ficha: e.target.value })}
                placeholder="ID de la ficha clínica asociada"
              />
              <p className="mt-1 text-xs text-pastel-ink/70 dark:text-gray-400">
                Si ya creaste una ficha clínica para esta cita, ingresa su ID aquí para asociarla.
              </p>
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={() => { setShowCompletarModal(false); setSelectedCitaId(null); setCompletarData({ observaciones: '', id_ficha: '' }); }}>
            Cancelar
          </Button>
          <Button onClick={handleCompletar}>
            Completar Cita
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Rechazar Cita */}
      <Modal isOpen={showRejectModal} onClose={() => { setShowRejectModal(false); setSelectedCitaId(null); setRejectMotivo(''); }} size="md">
        <ModalHeader onClose={() => { setShowRejectModal(false); setSelectedCitaId(null); setRejectMotivo(''); }}>
          Rechazar Cita
        </ModalHeader>
        <ModalContent>
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Por favor, proporciona el motivo por el cual se rechaza esta cita:
            </p>
            <textarea
              value={rejectMotivo}
              onChange={(e) => setRejectMotivo(e.target.value)}
              rows={4}
              className="input resize-none"
              placeholder="Ingrese el motivo del rechazo (mínimo 10 caracteres)..."
              autoFocus
              required
              minLength={10}
            />
            {rejectMotivo.length > 0 && rejectMotivo.length < 10 && (
              <p className="mt-1 text-xs text-yellow-600 dark:text-yellow-400">
                El motivo debe tener al menos 10 caracteres ({rejectMotivo.length}/10)
              </p>
            )}
          </div>
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={() => { setShowRejectModal(false); setSelectedCitaId(null); setRejectMotivo(''); }}>
            Cancelar
          </Button>
          <Button type="button" variant="danger" onClick={handleRechazar}>
            <XCircle className="w-4 h-4 mr-2" />
            Rechazar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Ver Detalles */}
      <Modal isOpen={showDetailModal} onClose={() => { setShowDetailModal(false); setSelectedCita(null); }} size="lg">
        <ModalHeader onClose={() => { setShowDetailModal(false); setSelectedCita(null); }}>
          Detalles de la Cita
        </ModalHeader>
        <ModalContent>
          {loadingDetail ? (
            <div className="flex justify-center items-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-primary-600" />
            </div>
          ) : selectedCita ? (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Información General</h3>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">Estado</label>
                      <div className="mt-1">
                        {getEstadoBadge(selectedCita.estado)}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">Tipo de Cita</label>
                      <div className="mt-1">
                        {getTipoCitaBadge(selectedCita.tipo_cita || selectedCita.tipoCita)}
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">Fecha de Solicitud</label>
                      <p className="mt-1 text-sm text-pastel-ink dark:text-white">
                        {selectedCita.fecha_solicitud 
                          ? new Date(selectedCita.fecha_solicitud).toLocaleDateString('es-ES', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '-'}
                      </p>
                    </div>
                    {selectedCita.fecha_aprobacion && (
                      <div>
                        <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">Fecha de Aprobación</label>
                        <p className="mt-1 text-sm text-pastel-ink dark:text-white">
                          {new Date(selectedCita.fecha_aprobacion).toLocaleDateString('es-ES', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Fecha y Hora</h3>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {(selectedCita.estado === 'PENDIENTE_APROBACION' || String(selectedCita.estado || '').includes('PENDIENTE_APROBACION')) &&
                      (selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta || selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta) && (
                      <div className="bg-sky-50 dark:bg-sky-900/25 border border-sky-200 dark:border-sky-800 rounded-lg p-3">
                        <div className="flex items-start space-x-2">
                          <Clock className="w-4 h-4 text-sky-700 dark:text-sky-300 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-sky-900 dark:text-sky-200 mb-1">
                              Fecha y hora tentativa solicitada por el cliente
                            </p>
                            <div className="space-y-1 text-sm text-sky-800 dark:text-sky-300">
                              {(selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta) && (
                                <div className="flex items-center space-x-2">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {new Date(selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta).toLocaleDateString('es-ES', {
                                      weekday: 'long',
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </span>
                                </div>
                              )}
                              {(selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta) && (
                                <div className="flex items-center space-x-2 font-medium">
                                  <Clock className="w-3 h-3" />
                                  <span>{selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta ? (
                      <>
                        {/* Mostrar si es fecha propuesta original (antes de confirmar) o confirmada */}
                        {selectedCita.estado === 'APROBADA' && !selectedCita.fecha_confirmacion && (
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mb-3">
                            <div className="flex items-start space-x-2">
                              <Info className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                              <div className="flex-1">
                                <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300 mb-1">
                                  Fecha y hora propuesta por el cliente (referencia):
                                </p>
                                <div className="space-y-1 text-xs text-yellow-700 dark:text-yellow-400">
                                  <div className="flex items-center space-x-2">
                                    <Calendar className="w-3 h-3" />
                                    <span>
                                      {new Date(selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta).toLocaleDateString('es-ES', { 
                                        year: 'numeric', 
                                        month: 'long', 
                                        day: 'numeric'
                                      })}
                                    </span>
                                  </div>
                                  {(selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta) && (
                                    <div className="flex items-center space-x-2">
                                      <Clock className="w-3 h-3" />
                                      <span>{selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">
                            {selectedCita.fecha_confirmacion ? 'Fecha Confirmada' : 'Fecha'}
                          </label>
                          <p className="mt-1 text-sm text-pastel-ink dark:text-white">
                            {new Date(selectedCita.fecha_cita_propuesta || selectedCita.fechaCitaPropuesta).toLocaleDateString('es-ES', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                        {selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta ? (
                          <div>
                            <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">
                              {selectedCita.fecha_confirmacion ? 'Hora Confirmada' : 'Hora'}
                            </label>
                            <p className="mt-1 text-sm text-pastel-ink dark:text-white">
                              {selectedCita.hora_cita_propuesta || selectedCita.horaCitaPropuesta}
                            </p>
                          </div>
                        ) : null}
                        {selectedCita.fecha_confirmacion && (
                          <div>
                            <label className="text-xs font-medium text-pastel-ink/70 dark:text-gray-400">Confirmada el</label>
                            <p className="mt-1 text-sm text-pastel-ink dark:text-white">
                              {new Date(selectedCita.fecha_confirmacion).toLocaleDateString('es-ES', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </p>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-pastel-ink/70 dark:text-gray-400">Fecha y hora no asignadas</p>
                    )}
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Mascota</h3>
                </CardHeader>
                <CardContent>
                  {selectedCita.mascota ? (
                    <div className="flex items-center space-x-3">
                      <Heart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                      <div>
                        <p className="text-sm font-medium text-pastel-ink dark:text-white">
                          {selectedCita.mascota.nombre}
                        </p>
                        <p className="text-xs text-pastel-ink/70 dark:text-gray-400">
                          {selectedCita.mascota.especie} - {selectedCita.mascota.raza}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-pastel-ink/70 dark:text-gray-400">No hay mascota asociada</p>
                  )}
                </CardContent>
              </Card>

              {selectedCita.clinica && (
                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Clínica</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-3">
                      <Building2 className="w-5 h-5 text-pastel-ink/60 dark:text-gray-500" />
                      <div>
                        <p className="text-sm font-medium text-pastel-ink dark:text-white">
                          {selectedCita.clinica.nombre || selectedCita.clinica.name}
                        </p>
                        {selectedCita.clinica.direccion && (
                          <p className="text-xs text-pastel-ink/70 dark:text-gray-400">
                            {selectedCita.clinica.direccion}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedCita.veterinario && selectedCita.veterinario.usuario && (
                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Veterinario</h3>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center space-x-3">
                      <Avatar user={selectedCita.veterinario.usuario} size="md" />
                      <p className="text-sm font-medium text-pastel-ink dark:text-white">
                        {selectedCita.veterinario.usuario.nombre} {selectedCita.veterinario.usuario.apellido || ''}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {selectedCita.descripcion_cita && (
                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-pastel-ink dark:text-white">Descripción</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-pastel-ink dark:text-gray-300 whitespace-pre-wrap">
                      {selectedCita.descripcion_cita}
                    </p>
                  </CardContent>
                </Card>
              )}

              {selectedCita.motivo_rechazo && (
                <Card>
                  <CardHeader>
                    <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">Motivo de Rechazo</h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-pastel-ink dark:text-gray-300 whitespace-pre-wrap">
                      {selectedCita.motivo_rechazo}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <p className="text-sm text-pastel-ink/70 dark:text-gray-400">No se pudieron cargar los detalles de la cita</p>
          )}
        </ModalContent>
        <ModalFooter>
          <div className="flex items-center justify-between w-full">
            {isDueno && selectedCita && (selectedCita.id_ficha || selectedCita.idFicha) && (
              <Button
                variant="primary"
                disabled={downloadingPDF === (selectedCita.id_ficha || selectedCita.idFicha)}
                onClick={async () => {
                  const fichaId = selectedCita.id_ficha || selectedCita.idFicha
                  try {
                    setDownloadingPDF(fichaId)
                    setError(null)
                    await fichaService.descargarPDF(fichaId)
                  } catch (err) {
                    console.error('Error al descargar PDF:', err)
                    const errorMessage = err.response?.data?.error || err.message || 'Error al descargar el PDF de la ficha clínica'
                    setError(errorMessage)
                  } finally {
                    setDownloadingPDF(null)
                  }
                }}
              >
                {downloadingPDF === (selectedCita.id_ficha || selectedCita.idFicha) ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Descargando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Descargar Ficha Clínica PDF
                  </>
                )}
              </Button>
            )}
            <Button variant="secondary" onClick={() => { setShowDetailModal(false); setSelectedCita(null); }}>
              Cerrar
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  )
}
